/**
 * SPLH Comparison Dashboard
 * Multi-location heatmap showing Sales Per Labour Hour by hour of day
 * Only rendered when QSR industry is selected
 * Click a location row to open drill-down panel
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Flame, TrendingUp, TrendingDown, MapPin, Car, Clock,
  ChevronDown, ChevronUp, Trophy, AlertTriangle, Zap,
  Users, DollarSign, Utensils, Phone, ExternalLink
} from 'lucide-react';
import { QSR_DEMO_LOCATIONS, QSR_BENCHMARKS } from '@/config/qsrConfig';
import { useClient } from '@/contexts/ClientContext';

const HOURS = [
  '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
  '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM',
  '6 PM', '7 PM', '8 PM', '9 PM', '10 PM'
];

// Seeded hourly SPLH data per location (realistic QSR patterns)
const HOURLY_SPLH = {
  'tdg-penrith': [28, 42, 55, 48, 52, 68, 92, 88, 62, 45, 52, 72, 85, 78, 65, 55, 38],
  'tdg-footscray': [0, 32, 42, 38, 40, 55, 74, 72, 52, 38, 42, 58, 68, 62, 52, 42, 30],
  'tdg-southport': [30, 48, 58, 52, 55, 72, 98, 95, 68, 48, 58, 78, 92, 82, 70, 58, 42],
  'tdg-joondalup': [0, 38, 48, 44, 48, 62, 82, 78, 58, 42, 48, 65, 75, 68, 58, 48, 35],
  'tdg-prospect': [0, 35, 45, 40, 44, 58, 78, 72, 52, 40, 44, 60, 70, 64, 54, 44, 32],
};

// Mock shift roster data per location
const SHIFT_ROSTER = {
  'tdg-penrith': [
    { name: 'Sarah Mitchell', role: 'Store Manager', shift: '6:00 AM – 2:30 PM', station: 'Floor / Office', status: 'on-shift' },
    { name: 'Jake Torres', role: 'Shift Supervisor', shift: '6:00 AM – 2:30 PM', station: 'Drive-Through', status: 'on-shift' },
    { name: 'Mia Chen', role: 'Crew', shift: '6:00 AM – 11:00 AM', station: 'Grill', status: 'on-shift' },
    { name: 'Aiden Patel', role: 'Crew', shift: '6:00 AM – 11:00 AM', station: 'Prep', status: 'on-shift' },
    { name: 'Sophie Brown', role: 'Crew', shift: '10:00 AM – 3:00 PM', station: 'Front Counter', status: 'on-shift' },
    { name: 'Tom Nguyen', role: 'Shift Supervisor', shift: '11:00 AM – 7:30 PM', station: 'Drive-Through', status: 'upcoming' },
    { name: 'Chloe Lee', role: 'Crew', shift: '11:00 AM – 7:30 PM', station: 'Grill', status: 'upcoming' },
    { name: 'Ryan Davis', role: 'Crew', shift: '11:00 AM – 4:00 PM', station: 'Drive-Through Runner', status: 'upcoming' },
    { name: 'Lily Adams', role: 'Crew', shift: '4:00 PM – 10:30 PM', station: 'Front Counter', status: 'later' },
    { name: 'Ben Wilson', role: 'Crew', shift: '4:00 PM – 10:30 PM', station: 'Grill', status: 'later' },
    { name: 'Emma Foster', role: 'Shift Supervisor', shift: '4:00 PM – 11:00 PM', station: 'Floor', status: 'later' },
    { name: 'Noah Martin', role: 'Crew', shift: '5:00 PM – 10:30 PM', station: 'Drive-Through', status: 'later' },
  ],
  'tdg-footscray': [
    { name: 'Michael Chen', role: 'Store Manager', shift: '7:00 AM – 3:30 PM', station: 'Floor / Office', status: 'on-shift' },
    { name: 'Grace Kim', role: 'Shift Supervisor', shift: '7:00 AM – 3:30 PM', station: 'Front Counter', status: 'on-shift' },
    { name: 'Daniel Wu', role: 'Crew', shift: '7:00 AM – 12:00 PM', station: 'Grill', status: 'on-shift' },
    { name: 'Zoe Tran', role: 'Crew', shift: '7:00 AM – 12:00 PM', station: 'Prep', status: 'on-shift' },
    { name: 'Lucas Pham', role: 'Shift Supervisor', shift: '11:00 AM – 7:30 PM', station: 'Floor', status: 'upcoming' },
    { name: 'Ava Huang', role: 'Crew', shift: '11:00 AM – 7:30 PM', station: 'Grill', status: 'upcoming' },
    { name: 'Jack Li', role: 'Crew', shift: '4:00 PM – 10:00 PM', station: 'Front Counter', status: 'later' },
    { name: 'Mia Wang', role: 'Crew', shift: '4:00 PM – 10:00 PM', station: 'Grill', status: 'later' },
  ],
  'tdg-southport': [
    { name: 'Emma Thompson', role: 'Store Manager', shift: '6:00 AM – 2:30 PM', station: 'Floor / Office', status: 'on-shift' },
    { name: 'James Hart', role: 'Shift Supervisor', shift: '6:00 AM – 2:30 PM', station: 'Drive-Through', status: 'on-shift' },
    { name: 'Olivia Scott', role: 'Crew', shift: '6:00 AM – 11:00 AM', station: 'Grill', status: 'on-shift' },
    { name: 'Mason Reed', role: 'Crew', shift: '6:00 AM – 11:00 AM', station: 'Prep', status: 'on-shift' },
    { name: 'Ella Clarke', role: 'Crew', shift: '10:00 AM – 3:00 PM', station: 'Front Counter', status: 'on-shift' },
    { name: 'Will Baker', role: 'Crew', shift: '10:00 AM – 3:00 PM', station: 'Drive-Through Runner', status: 'on-shift' },
    { name: 'Isla Young', role: 'Shift Supervisor', shift: '11:00 AM – 7:30 PM', station: 'Drive-Through', status: 'upcoming' },
    { name: 'Harry Kelly', role: 'Crew', shift: '11:00 AM – 7:30 PM', station: 'Grill', status: 'upcoming' },
    { name: 'Amelia Ross', role: 'Crew', shift: '11:30 AM – 5:00 PM', station: 'Front Counter', status: 'upcoming' },
    { name: 'Liam Cooper', role: 'Shift Supervisor', shift: '4:00 PM – 11:00 PM', station: 'Floor', status: 'later' },
    { name: 'Charlotte Evans', role: 'Crew', shift: '4:00 PM – 10:30 PM', station: 'Grill', status: 'later' },
    { name: 'Ethan Murray', role: 'Crew', shift: '4:00 PM – 10:30 PM', station: 'Drive-Through', status: 'later' },
    { name: 'Sophie Turner', role: 'Crew', shift: '5:00 PM – 10:30 PM', station: 'Prep', status: 'later' },
    { name: 'Oscar White', role: 'Crew', shift: '5:00 PM – 11:00 PM', station: 'Front Counter', status: 'later' },
  ],
  'tdg-joondalup': [
    { name: 'David Wilson', role: 'Store Manager', shift: '7:00 AM – 3:30 PM', station: 'Floor / Office', status: 'on-shift' },
    { name: 'Aria Zhang', role: 'Shift Supervisor', shift: '7:00 AM – 3:30 PM', station: 'Front Counter', status: 'on-shift' },
    { name: 'Leo Sharma', role: 'Crew', shift: '7:00 AM – 12:00 PM', station: 'Grill', status: 'on-shift' },
    { name: 'Nora Tan', role: 'Crew', shift: '7:00 AM – 12:00 PM', station: 'Prep', status: 'on-shift' },
    { name: 'Max Peters', role: 'Shift Supervisor', shift: '11:00 AM – 7:30 PM', station: 'Floor', status: 'upcoming' },
    { name: 'Eva Moore', role: 'Crew', shift: '11:00 AM – 7:30 PM', station: 'Grill', status: 'upcoming' },
    { name: 'Sam Burke', role: 'Crew', shift: '11:00 AM – 4:00 PM', station: 'Front Counter', status: 'upcoming' },
    { name: 'Ruby Hayes', role: 'Crew', shift: '4:00 PM – 10:00 PM', station: 'Front Counter', status: 'later' },
    { name: 'Kai Brooks', role: 'Crew', shift: '4:00 PM – 10:00 PM', station: 'Grill', status: 'later' },
  ],
  'tdg-prospect': [
    { name: 'Lauren Hayes', role: 'Store Manager', shift: '7:00 AM – 3:30 PM', station: 'Floor / Office', status: 'on-shift' },
    { name: 'Tyler Rose', role: 'Shift Supervisor', shift: '7:00 AM – 3:30 PM', station: 'Front Counter', status: 'on-shift' },
    { name: 'Zara Noor', role: 'Crew', shift: '7:00 AM – 12:00 PM', station: 'Grill', status: 'on-shift' },
    { name: 'Felix Grant', role: 'Crew', shift: '10:00 AM – 3:00 PM', station: 'Prep', status: 'on-shift' },
    { name: 'Ivy Walsh', role: 'Shift Supervisor', shift: '11:00 AM – 7:30 PM', station: 'Floor', status: 'upcoming' },
    { name: 'Ollie Stone', role: 'Crew', shift: '11:00 AM – 7:30 PM', station: 'Grill', status: 'upcoming' },
    { name: 'Piper Lane', role: 'Crew', shift: '4:00 PM – 10:00 PM', station: 'Front Counter', status: 'later' },
  ],
};

// Mock hourly revenue data
const HOURLY_REVENUE = {
  'tdg-penrith': [320, 680, 1100, 850, 920, 1450, 2100, 1950, 1200, 780, 920, 1500, 1800, 1600, 1250, 980, 600],
  'tdg-footscray': [0, 380, 580, 460, 500, 780, 1200, 1100, 720, 460, 560, 820, 1050, 900, 720, 560, 370],
  'tdg-southport': [380, 780, 1250, 950, 1020, 1600, 2400, 2250, 1400, 880, 1080, 1720, 2050, 1800, 1350, 1050, 680],
  'tdg-joondalup': [0, 480, 720, 560, 620, 980, 1500, 1380, 880, 540, 680, 1050, 1300, 1100, 880, 680, 450],
  'tdg-prospect': [0, 420, 620, 480, 540, 850, 1250, 1150, 750, 460, 580, 900, 1100, 950, 760, 580, 380],
};

const getSPLHColor = (value) => {
  if (value === 0) return 'bg-muted/30 text-muted-foreground/40';
  if (value >= QSR_BENCHMARKS.splh.excellent) return 'bg-emerald-500/90 text-white font-semibold';
  if (value >= QSR_BENCHMARKS.splh.good) return 'bg-emerald-400/70 text-white';
  if (value >= QSR_BENCHMARKS.splh.average) return 'bg-amber-400/70 text-amber-950';
  if (value >= QSR_BENCHMARKS.splh.poor) return 'bg-orange-400/70 text-orange-950';
  return 'bg-red-400/70 text-white';
};

const getShiftStatusColor = (status) => {
  if (status === 'on-shift') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (status === 'upcoming') return 'bg-blue-100 text-blue-800 border-blue-200';
  return 'bg-muted text-muted-foreground border-border';
};

const getShiftStatusLabel = (status) => {
  if (status === 'on-shift') return 'On Shift';
  if (status === 'upcoming') return 'Next Up';
  return 'Later';
};

const LocationRow = ({ location, data, isSelected, onClick }) => (
  <div
    className={`flex items-center gap-0 cursor-pointer transition-all hover:ring-1 hover:ring-primary/50 rounded-sm ${isSelected ? 'ring-2 ring-primary rounded-sm' : ''}`}
    onClick={() => onClick(location)}
    data-testid={`splh-row-${location.id}`}
  >
    <div className="w-[100px] sm:w-[120px] shrink-0 pr-2 flex items-center gap-1.5 text-xs truncate">
      {location.type === 'drive_through' && <Car className="w-3 h-3 text-amber-600 shrink-0" />}
      <span className="font-medium truncate">{location.shortName}</span>
      <span className="text-[10px] text-muted-foreground">{location.state}</span>
    </div>
    <div className="flex-1 flex gap-px">
      {data.map((val, i) => (
        <div
          key={`${val?.id ?? val?.name ?? val?.title ?? 'val'}-${i}`}
          className={`flex-1 h-8 flex items-center justify-center text-[10px] transition-colors ${getSPLHColor(val)}`}
          title={`${location.shortName} at ${HOURS[i]}: $${val} SPLH`}
        >
          {val > 0 ? val : '-'}
        </div>
      ))}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, subtext, color = 'text-foreground' }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40">
    <Icon className={`w-4 h-4 ${color} shrink-0`} />
    <div className="min-w-0">
      <div className={`text-sm font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground truncate">{label}</div>
      {subtext && <div className="text-[10px] text-muted-foreground/70">{subtext}</div>}
    </div>
  </div>
);

// Location Drill-Down Panel
const LocationDrillDown = ({ location, onClose }) => {
  if (!location) return null;

  const hourly = HOURLY_SPLH[location.id] || [];
  const revenue = HOURLY_REVENUE[location.id] || [];
  const roster = SHIFT_ROSTER[location.id] || [];
  const activeHours = hourly.filter(v => v > 0);
  const avgSPLH = activeHours.length > 0 ? (activeHours.reduce((s, v) => s + v, 0) / activeHours.length).toFixed(1) : '0';
  const totalRevenue = revenue.reduce((s, v) => s + v, 0);
  const peakSPLH = Math.max(...hourly);
  const peakHour = HOURS[hourly.indexOf(peakSPLH)];
  const isDriveThru = location.type === 'drive_through';

  // Shift breakdown
  const shifts = [
    { name: 'Opening', time: '6-10 AM', hours: hourly.slice(0, 4), rev: revenue.slice(0, 4) },
    { name: 'Lunch Rush', time: '11 AM-2 PM', hours: hourly.slice(5, 8), rev: revenue.slice(5, 8) },
    { name: 'Afternoon', time: '2-5 PM', hours: hourly.slice(8, 11), rev: revenue.slice(8, 11) },
    { name: 'Dinner', time: '5-10 PM', hours: hourly.slice(11, 17), rev: revenue.slice(11, 17) },
  ];

  return (
    <Sheet open={!!location} onOpenChange={() => onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="location-drilldown-panel">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-base font-bold">{location.name}</div>
              <div className="text-xs text-muted-foreground font-normal">{location.address}, {location.suburb} {location.state} {location.postcode}</div>
            </div>
          </SheetTitle>
        </SheetHeader>

        {/* Quick Stats */}
 <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg bg-muted/50 p-2.5 text-center">
            <div className="text-lg font-bold text-amber-600">${avgSPLH}</div>
            <div className="text-[10px] text-muted-foreground">Avg SPLH</div>
          </div>
          <div className="rounded-lg bg-muted/50 p-2.5 text-center">
            <div className="text-lg font-bold text-emerald-600">${totalRevenue.toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground">Daily Revenue</div>
          </div>
          <div className="rounded-lg bg-muted/50 p-2.5 text-center">
            <div className="text-lg font-bold text-blue-600">{roster.length}</div>
            <div className="text-[10px] text-muted-foreground">Staff Today</div>
          </div>
        </div>

        {/* Location Details */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {location.features.map(f => (
            <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
          ))}
          <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
            Rank #{location.performance.rank} of 5
          </Badge>
          <Badge className={`text-[10px] ${location.performance.trend === 'up' ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}`}>
            {location.performance.trend === 'up' ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
            {location.performance.changePercent}%
          </Badge>
        </div>

        {/* Shift Breakdown */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Shift Performance Breakdown
          </h4>
          <div className="space-y-2">
            {shifts.map(shift => {
              const shiftAvgSPLH = shift.hours.filter(v => v > 0).length > 0
                ? (shift.hours.filter(v => v > 0).reduce((s, v) => s + v, 0) / shift.hours.filter(v => v > 0).length).toFixed(0)
                : 0;
              const shiftRev = shift.rev.reduce((s, v) => s + v, 0);
              const isLunch = shift.name === 'Lunch Rush';
              return (
                <div key={shift.name} className={`rounded-lg p-2.5 flex items-center justify-between ${isLunch ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50' : 'bg-muted/30'}`}>
                  <div>
                    <div className="text-xs font-semibold">{shift.name}</div>
                    <div className="text-[10px] text-muted-foreground">{shift.time}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs font-bold">${shiftAvgSPLH}</div>
                      <div className="text-[10px] text-muted-foreground">SPLH</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-bold">${shiftRev.toLocaleString()}</div>
                      <div className="text-[10px] text-muted-foreground">Revenue</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hourly Revenue Chart (text-based mini bars) */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" />
            Hourly Revenue
          </h4>
          <div className="space-y-1">
            {HOURS.map((h, i) => {
              const val = revenue[i] || 0;
              const maxRev = Math.max(...revenue);
              const pct = maxRev > 0 ? (val / maxRev) * 100 : 0;
              return (
                <div key={h} className="flex items-center gap-2 text-[10px]">
                  <span className="w-10 text-muted-foreground shrink-0 text-right">{h.replace(' AM', 'a').replace(' PM', 'p')}</span>
                  <div className="flex-1 h-4 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-14 text-right font-medium">${val > 0 ? val.toLocaleString() : '-'}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Drive-Through Metrics (if applicable) */}
        {isDriveThru && location.metrics.driveThrough && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
              <Car className="w-3.5 h-3.5 text-amber-600" />
              Drive-Through Metrics
            </h4>
 <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/30 p-2.5">
                <div className="text-sm font-bold text-amber-700">{location.metrics.driveThrough.avgWaitTime} min</div>
                <div className="text-[10px] text-muted-foreground">Avg Wait Time</div>
                <div className="text-[10px] text-muted-foreground/70">Target: {QSR_BENCHMARKS.driveThrough.waitTime.good} min</div>
              </div>
              <div className="rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/30 p-2.5">
                <div className="text-sm font-bold text-amber-700">{location.metrics.driveThrough.throughputPerHour}/hr</div>
                <div className="text-[10px] text-muted-foreground">Cars Served</div>
                <div className="text-[10px] text-muted-foreground/70">Peak: {location.metrics.driveThrough.peakThroughput}/hr</div>
              </div>
              <div className="rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/30 p-2.5">
                <div className="text-sm font-bold text-emerald-700">{location.metrics.driveThrough.accuracy}%</div>
                <div className="text-[10px] text-muted-foreground">Order Accuracy</div>
              </div>
              <div className="rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/30 p-2.5">
                <div className="text-sm font-bold text-blue-700">{location.metrics.driveThrough.carsServedDaily}</div>
                <div className="text-[10px] text-muted-foreground">Cars Today</div>
              </div>
            </div>
          </div>
        )}

        {/* Staff Roster */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Today's Roster ({roster.length} staff)
          </h4>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {roster.map((staff, i) => (
              <div key={`${staff?.id ?? staff?.name ?? staff?.title ?? 'staff'}-${i}`} className="flex items-center justify-between rounded-lg bg-muted/30 px-2.5 py-1.5" data-testid={`roster-staff-${i}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{staff.name}</div>
                  <div className="text-[10px] text-muted-foreground">{staff.role} — {staff.station}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-muted-foreground">{staff.shift}</span>
                  <Badge variant="outline" className={`text-[9px] py-0 ${getShiftStatusColor(staff.status)}`}>
                    {getShiftStatusLabel(staff.status)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Location Contact */}
        <div className="rounded-lg bg-muted/30 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
            <div>
              <div className="text-xs font-medium">{location.manager}</div>
              <div className="text-[10px] text-muted-foreground">{location.phone}</div>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px]">
            {location.openingHours}
          </Badge>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const SPLHComparisonDashboard = () => {
  const { currentLocation } = useClient();
  const [expanded, setExpanded] = useState(true);
  const [drillDownLocation, setDrillDownLocation] = useState(null);

  const stats = useMemo(() => {
    const locData = QSR_DEMO_LOCATIONS.map(loc => {
      const hourly = HOURLY_SPLH[loc.id] || [];
      const activeHours = hourly.filter(v => v > 0);
      const avg = activeHours.length > 0
        ? activeHours.reduce((s, v) => s + v, 0) / activeHours.length
        : 0;
      const peak = Math.max(...hourly);
      const peakHour = HOURS[hourly.indexOf(peak)];
      return { ...loc, avg: avg.toFixed(1), peak, peakHour, hourly };
    });

    const topPerformer = locData.reduce((a, b) => parseFloat(a.avg) > parseFloat(b.avg) ? a : b);
    const lowPerformer = locData.reduce((a, b) => parseFloat(a.avg) < parseFloat(b.avg) ? a : b);

    let bestHourVal = 0;
    let bestHourLabel = '';
    HOURS.forEach((h, i) => {
      const total = locData.reduce((s, l) => s + (l.hourly[i] || 0), 0);
      if (total > bestHourVal) {
        bestHourVal = total;
        bestHourLabel = h;
      }
    });

    const networkAvg = (locData.reduce((s, l) => s + parseFloat(l.avg), 0) / locData.length).toFixed(1);

    return { locData, topPerformer, lowPerformer, bestHourLabel, networkAvg };
  }, []);

  return (
    <>
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="splh-comparison-dashboard">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">SPLH Heatmap</CardTitle>
                <p className="text-[10px] text-muted-foreground">Click any location row for detailed drill-down</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                <TrendingUp className="w-3 h-3 mr-1" />
                Network Avg: ${stats.networkAvg}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="h-7 w-7 p-0"
                data-testid="splh-toggle"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="pt-2 px-4 pb-4">
            {/* Summary Stats */}
 <div className="grid grid-cols-2 gap-2 mb-4">
              <StatCard icon={Trophy} label="Top Performer" value={`$${stats.topPerformer.avg}`} subtext={stats.topPerformer.shortName} color="text-emerald-600" />
              <StatCard icon={AlertTriangle} label="Needs Attention" value={`$${stats.lowPerformer.avg}`} subtext={stats.lowPerformer.shortName} color="text-amber-600" />
              <StatCard icon={Clock} label="Peak Hour" value={stats.bestHourLabel} subtext="Highest network revenue" color="text-blue-600" />
              <StatCard icon={Zap} label="Benchmark Target" value={`$${QSR_BENCHMARKS.splh.good}`} subtext={QSR_BENCHMARKS.splh.source.split('(')[0]} color="text-purple-600" />
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mb-3 text-[10px] text-muted-foreground">
              <span className="font-medium">SPLH:</span>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-emerald-500/90" /> $70+</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-emerald-400/70" /> $60-69</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-amber-400/70" /> $50-59</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-orange-400/70" /> $40-49</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-red-400/70" /> &lt;$40</div>
              <div className="flex items-center gap-1 ml-2"><Car className="w-3 h-3 text-amber-600" /> DT</div>
            </div>

            {/* Heatmap Grid */}
            <div className="overflow-x-auto">
              <div className="flex items-center gap-0 mb-1">
                <div className="w-[100px] sm:w-[120px] shrink-0" />
                <div className="flex-1 flex gap-px">
                  {HOURS.map(h => (
                    <div key={h} className="flex-1 text-center text-[9px] text-muted-foreground font-medium">
                      {h.replace(' AM', 'a').replace(' PM', 'p')}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-px">
                {stats.locData.map(loc => (
                  <LocationRow
                    key={loc.id}
                    location={loc}
                    data={loc.hourly}
                    isSelected={currentLocation?.id === loc.id}
                    onClick={setDrillDownLocation}
                  />
                ))}
              </div>
            </div>

            {/* Drive-Through Comparison */}
            <div className="mt-4 pt-3 border-t border-border/50">
              <div className="flex items-center gap-1.5 mb-2">
                <Car className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-semibold">Drive-Through Performance</span>
              </div>
 <div className="grid grid-cols-2 gap-3">
                {QSR_DEMO_LOCATIONS.filter(l => l.type === 'drive_through').map(loc => (
                  <div
                    key={loc.id}
                    className="rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 p-3 cursor-pointer hover:ring-1 hover:ring-amber-400 transition-all"
                    onClick={() => setDrillDownLocation(loc)}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold">{loc.shortName}</span>
                      <Badge variant="outline" className="text-[10px]">{loc.state}</Badge>
                    </div>
 <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-sm font-bold text-amber-700">{loc.metrics.driveThrough.avgWaitTime}m</div>
                        <div className="text-[10px] text-muted-foreground">Wait Time</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-amber-700">{loc.metrics.driveThrough.throughputPerHour}</div>
                        <div className="text-[10px] text-muted-foreground">Cars/Hr</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-emerald-600">{loc.metrics.driveThrough.accuracy}%</div>
                        <div className="text-[10px] text-muted-foreground">Accuracy</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Location Drill-Down Panel */}
      <LocationDrillDown location={drillDownLocation} onClose={() => setDrillDownLocation(null)} />
    </>
  );
};

export default SPLHComparisonDashboard;
